import clsx from 'clsx';
import { forwardRef } from 'react';

import { Icon } from '../icon/Icon';
import { Input, InputProps } from '../input/Input';
import styles from './Select.module.scss';

export interface SelectProps {
  /** Whether the dropdown is currently open. Drives the chevron direction (`Up` when active, `Down` when inactive). */
  isActive: boolean;
  /** Props forwarded to the underlying `Input`. Use this to set `value`, `placeholder`, `onClick`, `onChange`, etc. */
  inputProps: InputProps;
  /**
   * When true (default), the input is rendered with `readOnly` so the
   * user can't type into it — useful for selects that only accept
   * predefined options.
   */
  isReadonlyInput?: boolean;
  /** Extra class forwarded to the wrapper. */
  className?: string;
}

/**
 * Trigger-only "select" control: a styled `Input` with a chevron that
 * flips direction based on `isActive`.
 *
 * This component does **not** render the dropdown panel itself —
 * compose it with `Dropdown` (or your own popover) and drive
 * `isActive` from open state. For a fully-managed select, build it on
 * top of `Dropdown`.
 */
export const Select = forwardRef<HTMLInputElement, SelectProps>(
  (
    {
      isActive,
      inputProps,
      isReadonlyInput = true,
      className,
      ...rest
    }: SelectProps,
    ref
  ) => {
    return (
      <div className={clsx(styles.root, className)} {...rest}>
        <Input
          data-testid="select-input"
          {...inputProps}
          ref={ref}
          readOnly={isReadonlyInput}
          rightIcon={
            <Icon
              iconName={isActive ? 'ChevronLightUp' : 'ChevronLightDown'}
              size={12}
            />
          }
        />
      </div>
    );
  }
);
